import React from 'react';

type AccessLevel = 'full' | 'restricted' | 'readonly';

interface PasswordSecurityProps {
  accessLevel: AccessLevel;
  setAccessLevel: React.Dispatch<React.SetStateAction<AccessLevel>>;

  handleToggleSetting: (key: any) => void;
  settings: {
    mfa: boolean;
    autoTimeout: boolean;
    ipRestriction: boolean;
    auditLogging: boolean;
    timeRestriction: boolean;
  };

  maxUsers: number | '';
  setMaxUsers: (value: number | '') => void;
  sessionDuration: string;
  setSessionDuration: (value: string) => void;
}

export const PasswordSecurity: React.FC<PasswordSecurityProps> = ({
  accessLevel,
  setAccessLevel,
  settings,
  handleToggleSetting,
  maxUsers,
  setMaxUsers,
  sessionDuration,
  setSessionDuration,
}) => {
  const handleReset = () => {
    setAccessLevel('full');
    setMaxUsers('');
    setSessionDuration('60');
    Object.keys(settings).forEach((key) => {
      handleToggleSetting(key as keyof typeof settings);
    });
  };

  return (
    <div className="">
      {/* Header */}
      <h2 className="text-xl font-semibold text-gray-800">Access Configuration</h2>
      <p className="text-sm text-gray-500 mb-6">
        Configure security settings and access restrictions
      </p>

      {/* Access Level */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <button
          onClick={() => setAccessLevel('full')}
          className={`p-4 border rounded-lg text-center ${
            accessLevel === 'full' ? 'border-pink-600 bg-pink-50' : 'border-gray-300 bg-white'
          }`}
        >
          <div className="font-medium">Full Access</div>
          <div className="text-xs text-gray-500">No restrictions</div>
        </button>
        <button
          onClick={() => setAccessLevel('restricted')}
          className={`p-4 border rounded-lg text-center ${
            accessLevel === 'restricted' ? 'border-pink-600 bg-pink-50' : 'border-gray-300 bg-white'
          }`}
        >
          <div className="font-medium">Restricted</div>
          <div className="text-xs text-gray-500">Limited access</div>
        </button>
        <button
          onClick={() => setAccessLevel('readonly')}
          className={`p-4 border rounded-lg text-center ${
            accessLevel === 'readonly' ? 'border-pink-600 bg-pink-50' : 'border-gray-300 bg-white'
          }`}
        >
          <div className="font-medium">Read Only</div>
          <div className="text-xs text-gray-500">View access only</div>
        </button>
      </div>

      {/* Security Settings */}
      <div className="border rounded-xl p-4 mb-6 bg-white shadow-sm">
        <h3 className="font-medium text-gray-800 mb-2">Security Settings</h3>
        <p className="text-xs text-gray-500 mb-4">Section description</p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.mfa}
              onChange={() => handleToggleSetting('mfa')}
              className="accent-pink-600"
            />
            Require Multi-Factor Authentication
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.ipRestriction}
              onChange={() => handleToggleSetting('ipRestriction')}
              className="accent-pink-600"
            />
            Enable IP address restrictions
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.autoTimeout}
              onChange={() => handleToggleSetting('autoTimeout')}
              className="accent-pink-600"
            />
            Enable automatic session timeout
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.auditLogging}
              onChange={() => handleToggleSetting('auditLogging')}
              className="accent-pink-600"
            />
            Enable detailed audit logging
          </label>
        </div>

        {/* Max Users & Session Duration */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Maximum Users</label>
            <input
              type="number"
              value={maxUsers}
              onChange={(e) => setMaxUsers(e.target.value ? Number(e.target.value) : '')}
              placeholder="Enter maximum number of users"
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">
              Text for description, requirement or error message
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Session Duration (minutes)</label>
            <select
              value={sessionDuration}
              onChange={(e) => setSessionDuration(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
              <option value="120">2 hours</option>
              <option value="240">4 hours</option>
            </select>
          </div>
        </div>

        {/* Time Restrictions */}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={settings.timeRestriction}
            onChange={() => handleToggleSetting('timeRestriction')}
            className="accent-pink-600"
          />
          Enable time-based access restrictions
        </label>
      </div>
    </div>
  );
};

export default PasswordSecurity;
